import { useEffect, useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

const margin = 12;
const gap = 8;
const dismissEvents = ['mousedown', 'touchstart', 'focusin'] as const;

// eslint-disable-next-line max-statements
const positionPopover = (trigger: HTMLButtonElement): CSSProperties => {
  const rect = trigger.getBoundingClientRect();
  const viewport = window.visualViewport;
  const bounds = viewport?.scale === 1 ? viewport
    : { width: window.innerWidth, height: window.innerHeight, offsetTop: 0, offsetLeft: 0 };
  const width = Math.max(0, Math.min(360, bounds.width - margin * 2));
  const lowerEdge = bounds.offsetTop + bounds.height - margin;
  const upperEdge = bounds.offsetTop + margin;
  const anchorAbove = Math.max(upperEdge, Math.min(rect.top - gap, lowerEdge));
  const anchorBelow = Math.max(upperEdge, Math.min(rect.bottom + gap, lowerEdge));
  const below = lowerEdge - anchorBelow;
  const above = anchorAbove - upperEdge;
  const opensAbove = below < 340 && above > below;

  return {
    width,
    left: Math.max(bounds.offsetLeft + margin,
      Math.min(rect.right - width, bounds.offsetLeft + bounds.width - width - margin)),
    ...(opensAbove ? { bottom: window.innerHeight - anchorAbove } : { top: anchorBelow }),
    maxHeight: Math.max(0, Math.min(520, opensAbove ? above : below)),
  };
};

// eslint-disable-next-line max-lines-per-function
export const useNotificationPopover = (
  open: boolean,
  trigger: RefObject<HTMLButtonElement | null>,
  popup: RefObject<HTMLDivElement | null>,
  close: () => void,
) => {
  const [position, setPosition] = useState<CSSProperties>({});

  // eslint-disable-next-line max-statements
  useLayoutEffect(() => {
    if (!open) return undefined;
    const viewport = window.visualViewport;
    const update = (event?: Event) => {
      if (event && viewport && viewport.scale !== 1) return;
      if (trigger.current) setPosition(positionPopover(trigger.current));
    };

    update();
    popup.current?.focus({ preventScroll: true });
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
    };
  }, [open, popup, trigger]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: Event) => {
      const target = event.target as Node;

      if (!trigger.current?.contains(target) && !popup.current?.contains(target)) close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      close();
      trigger.current?.focus({ preventScroll: true });
    };

    dismissEvents.forEach((name) => document.addEventListener(name, outside));
    document.addEventListener('keydown', escape);

    return () => {
      dismissEvents.forEach((name) => document.removeEventListener(name, outside));
      document.removeEventListener('keydown', escape);
    };
  }, [close, open, popup, trigger]);

  return position;
};
